import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
  IonSpinner
} from '@ionic/react';
import { settingsOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';
import { useProgress } from '../hooks/useProgress';
import { useStreak } from '../hooks/useStreak';
import { configService } from '../services/ConfigService';
import ProgressTracker from '../components/ProgressTracker';
import MascotMessage from '../components/MascotMessage';
import LessonCard from '../components/LessonCard';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './Home.css';

/**
 * Home Page - Main dashboard with progress and continue learning
 */
function Home() {
  const history = useHistory();
  const { config, lessons, loading, error } = useConfig();
  const { totalPoints, lessonsCompleted, lessonsTotal, isLessonCompleted, getLessonProgress } = useProgress();
  const { currentStreak } = useStreak();
  const [nextLesson, setNextLesson] = useState(null);

  useEffect(() => {
    // Find next uncompleted lesson
    if (lessons?.lessons && lessons.lessons.length > 0) {
      const next = lessons.lessons.find(lesson => {
        return !isLessonCompleted(lesson.id);
      });
      setNextLesson(next || lessons.lessons[0]);
    }
  }, [lessons, isLessonCompleted]);

  const handleContinue = () => {
    if (nextLesson) {
      history.push(`/lessons/${nextLesson.id}`);
    }
  };

  const getMascotMessage = () => {
    if (loading) {
      return "Loading your progress...";
    }
    const mascotConfig = configService.getMascotConfig();
    if (lessonsCompleted === 0) {
      return mascotConfig?.welcomeMessage || "Welcome! Let's start learning to code! 🚀";
    } else if (currentStreak >= 7) {
      return `Amazing ${currentStreak}-day streak! 🔥 Keep it up!`;
    } else if (totalPoints >= 1000) {
      return "You're doing great! Keep coding! 💪";
    } else {
      return "Ready for today's lesson? Let's go! 🎯";
    }
  };

  const getMascotState = () => {
    if (loading) return 'thinking';
    if (lessonsCompleted === 0) return 'wave';
    if (currentStreak >= 7) return 'celebrate';
    return 'happy';
  };

  // Loading state
  if (loading) {
    return (
      <IonPage>
        <IonContent className="home-content">
          <div className="home-loading">
            <div className="loading-spinner"></div>
            <p>Loading your coding journey...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Error state
  if (error) {
    return (
      <IonPage>
        <IonContent className="home-content">
          <div className="home-empty">
            <div className="home-empty-icon">😕</div>
            <h3>Something went wrong</h3>
            <p>We couldn't load your data. Please try again.</p>
            <IonButton onClick={() => window.location.reload()}>
              Retry
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const meta = seoService.getPageMeta('home');

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
      />
      <IonPage>
        <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>{configService.getAppInfo()?.displayName || 'Learn to Code'}</IonTitle>
          <IonButton slot="end" fill="clear" color="light" routerLink="/settings">
            <IonIcon icon={settingsOutline} />
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent className="home-content">
        <div className="home-container">
          {/* Mascot Welcome */}
          <MascotMessage
            message={getMascotMessage()}
            state={getMascotState()}
            size="large"
          />

          {/* Progress Section */}
          <ProgressTracker
            totalPoints={totalPoints || 0}
            currentStreak={currentStreak || 0}
            lessonsCompleted={lessonsCompleted || 0}
            totalLessons={lessonsTotal || lessons?.meta?.totalLessons || 50}
          />

          {/* Continue Learning */}
          {nextLesson && (
            <div className="continue-section">
              <h2>Continue Learning</h2>
              <LessonCard
                lesson={nextLesson}
                isCompleted={false}
                isLocked={false}
                progress={getLessonProgress(nextLesson.id)?.step || 0}
                onClick={handleContinue}
              />
            </div>
          )}

          {/* Quick Actions */}
          <div className="quick-actions">
            <IonButton expand="block" color="primary" routerLink="/lessons">
              📚 All Lessons
            </IonButton>
            <IonButton expand="block" fill="outline" color="primary" routerLink="/challenges">
              🎯 Challenges
            </IonButton>
            <IonButton expand="block" fill="outline" color="primary" routerLink="/playground">
              💻 Code Playground
            </IonButton>
            <IonButton expand="block" fill="outline" color="primary" routerLink="/interviews">
              💼 Interview Prep
            </IonButton>
          </div>

          {/* Daily Goal */}
          {configService.getGamification()?.enabled && (
            <div className="daily-goal">
              <h3>Daily Goal</h3>
              <p>Complete {configService.getGamification()?.dailyGoal || 3} lessons today</p>
              <div className="goal-progress">
                <div 
                  className="goal-fill"
                  style={{ 
                    width: `${Math.min(100, ((lessonsCompleted || 0) % (configService.getGamification()?.dailyGoal || 3)) / (configService.getGamification()?.dailyGoal || 3) * 100)}%` 
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Ad Banner (FREE version only) */}
        <AdBanner position="bottom" />
      </IonContent>
    </IonPage>
    </>
  );
}

export default Home;